import { useEffect, useState, useCallback } from "react";
import {
  fetchNotifications,
  unreadCount,
  markRead,
  markAllRead,
  type AppNotification,
} from "@/lib/notifications";
import { useProjects } from "@/state/project";
import { useGrid } from "@/state/grid";

/** Header notification bell with unread badge + dropdown feed (Task 7). */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const currentId = useProjects((s) => s.currentId);
  const setView = useGrid((s) => s.setView);

  const refresh = useCallback(async () => {
    setUnread(await unreadCount());
  }, []);

  // Poll the unread count; re-poll on project switch.
  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(t);
  }, [refresh, currentId]);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next) setItems(await fetchNotifications());
  }

  async function openItem(n: AppNotification) {
    if (!n.read_at) {
      await markRead(n.id);
      void refresh();
    }
    setOpen(false);
    if (n.link) {
      const v = n.link.replace(/^\//, "");
      if (["overview", "rfi", "sitemap", "qc", "punch", "tdd"].includes(v)) setView(v as never);
    }
  }

  return (
    <div className={`bell-wrap ${open ? "open" : ""}`}>
      <button className="iconbtn bell-btn" title="Notifications" onClick={toggle}>
        🔔
        {unread > 0 && <span className="bell-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="bell-menu">
          <div className="bell-head">
            <b>Notifications</b>
            {unread > 0 && (
              <button
                className="bell-clear"
                onClick={async () => {
                  await markAllRead();
                  setItems(await fetchNotifications());
                  void refresh();
                }}
              >
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="bell-empty">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                className={`bell-item ${n.read_at ? "" : "unread"}`}
                onClick={() => openItem(n)}
              >
                <span className="bell-msg">{n.message}</span>
                <span className="bell-time">{new Date(n.created_at).toLocaleString()}</span>
              </button>
            ))
          )}
        </div>
      )}
      <style>{BELL_CSS}</style>
    </div>
  );
}

const BELL_CSS = `
.bell-wrap{ position:relative; }
.bell-btn{ position:relative; }
.bell-badge{ position:absolute; top:-4px; right:-4px; min-width:16px; height:16px; padding:0 4px;
  background:var(--rust,#C0563B); color:#fff; border-radius:8px; font-size:10px; font-weight:700;
  display:flex; align-items:center; justify-content:center; }
.bell-menu{ position:absolute; top:calc(100% + 6px); right:0; width:320px; max-height:380px; overflow:auto; z-index:60;
  background:var(--panel,#0e1620); border:1px solid var(--line); border-radius:10px; padding:6px;
  box-shadow:0 12px 32px rgba(0,0,0,.45); }
.bell-head{ display:flex; align-items:center; justify-content:space-between; padding:6px 8px; font-size:13px; }
.bell-clear{ background:transparent; border:0; color:var(--steel,#3A719E); font-size:12px; cursor:pointer; }
.bell-empty{ padding:18px 8px; color:var(--dim); font-size:13px; text-align:center; }
.bell-item{ display:flex; flex-direction:column; gap:3px; width:100%; text-align:left; padding:9px 8px;
  background:transparent; border:0; border-radius:7px; color:var(--ink); cursor:pointer; }
.bell-item:hover{ background:var(--panel2,#141d27); }
.bell-item.unread{ background:rgba(58,113,158,.12); }
.bell-msg{ font-size:13px; line-height:1.35; }
.bell-time{ font-size:11px; color:var(--dim); }
`;
